import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, RowData, RowModel, SortingState, Table, useReactTable } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";

import useApiHelper from "../../../hooks/useApiHelper";
import useIsMobile from "../../../hooks/useIsMobile";
import { PageMode } from "../../../models/PagedTable";
import { DefaultResponseWithData } from "../../../models/system";
import { ButtonInput } from "../Inputs/ButtonInput";
import LoadingWheel from "../LoadingWheel";import React from "react";
;

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    // Property path the API should sort by when this column's header is clicked.
    // Defaults to the accessorKey, then the column id. Needed for display columns
    // and for accessors whose value isn't a real DB column (e.g. contact.fullname).
    sortKey?: string;
  }
}

// Mirrors how react-table derives a column id when none is given explicitly.
const resolveColumnId = (col: any): string | undefined =>
  col.id ??
  (typeof col.accessorKey === "string" ? col.accessorKey.replace(/\./g, "_") : undefined) ??
  (typeof col.header === "string" ? col.header : undefined);

// Flattens column groups and maps each leaf column id to the key the API should sort on.
const buildSortKeyMap = (columns: any[]): Record<string, string> => {
  const map: Record<string, string> = {};
  const walk = (cols: any[]) =>
    cols.forEach((col) => {
      if (col.columns) walk(col.columns);
      const id = resolveColumnId(col);
      if (id) map[id] = col.meta?.sortKey ?? col.accessorKey ?? id;
    });
  walk(columns ?? []);
  return map;
};

export interface IPagedResponse<T> extends DefaultResponseWithData<IPagedData<T>> { }

export interface IPagedData<T> {
  rows: T[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export interface IPagedTableActionButton<T> {
  label: string;
  onClick: (rows: RowModel<T>, table: Table<T>) => void;
  disabledOnNoRows?: boolean;
  isLoading?: boolean;
}

export interface IPagedModeToggle {
  label: string;
  pageMode?: PageMode;
  urlQuery?: string;
}

export interface IPagedTableProps<T> {
  url: string;
  columns: any;
  pageMode?: PageMode;
  queryLabel?: string;
  externalQuery?: string;
  queryKey?: string;
  modeToggles?: IPagedModeToggle[];
  customUrlQuery?: string;
  actionButtons?: IPagedTableActionButton<T>[];
  expandedRow?: (row: T) => JSX.Element;
  onRowClick?: (row: T) => void;
  showTotal?: boolean;
  additionalBody?: Record<string, any>;
  initialSort?: SortingState;
  setRowData?: (data: any) => void;
  rowColor?: (row: T) => string;
  cellPadding?: string;
  fontSize?: string;
  containerClassName?: string;
  condensedView?: boolean;
  insertEmptyRowUrl?: string;
}

export type DateRangeType = {
  fieldName: string;
  start: Date | null;
  end: Date | null;
}

const EMPTY_ROWS: any[] = [];

function useSkipper() {
  const shouldSkipRef = React.useRef(true)
  const shouldSkip = shouldSkipRef.current

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = React.useCallback(() => {
    shouldSkipRef.current = false
  }, [])

  React.useEffect(() => {
    shouldSkipRef.current = true
  })

  return [shouldSkip, skip] as const
}

export default function PagedTable<T>(tableConfig: IPagedTableProps<T>) {
  const { post } = useApiHelper();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState<string>("");
  const hasExternalQuery = tableConfig.externalQuery !== undefined;
  const [toggles, setToggles] = useState<boolean[]>(new Array(tableConfig.modeToggles ? tableConfig.modeToggles?.length : 0).fill(false));
  const [sorting, setSorting] = useState<SortingState>(tableConfig.initialSort ?? []);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { setRowData } = tableConfig;

  const debouncedQuery = useDebounce(query, 1000);
  const debouncedAdditionalBody = useDebounce(tableConfig.additionalBody, 300);
  const pageParams = [debouncedQuery, pageIndex, toggles, sorting, pageSize];
  
  const getPageMode = useCallback(() => {
    const pageModeToggles = tableConfig.modeToggles?.filter((toggle, i) => toggle.pageMode && toggles[i]);

    return !pageModeToggles || pageModeToggles.length === 0
      ? tableConfig.pageMode
      : pageModeToggles[0].pageMode;
  }, [tableConfig, toggles]);
  
  const currentPageMode = useMemo(() => getPageMode(), [getPageMode]);

  // Cleanup function to cancel pending requests
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (hasExternalQuery) {
      const external = tableConfig.externalQuery ?? "";
      if (external !== query) {
        setQuery(external);
      }
      return;
    }
  }, [hasExternalQuery, tableConfig.externalQuery, query]);

  const getUrl = useCallback(() => {
    const { url, customUrlQuery, modeToggles } = tableConfig;

    const queryString =
      modeToggles
        ?.filter(toggle => toggle.urlQuery)
        .map(toggle => toggle.urlQuery)
        .join("&") ?? "";

    const customQuery = customUrlQuery ? `&${customUrlQuery}` : "";
    const modeQuery = currentPageMode;

    let finalUrl = `${url}/${pageIndex}?pageMode=${modeQuery}`;

    if (pageSize) finalUrl += `&pageSize=${pageSize}`;

    // Add the queries if they exist
    if (debouncedQuery.trim()) finalUrl += `&query=${debouncedQuery}`;

    if (queryString) finalUrl += `&${queryString}`;

    // Append the custom query if provided
    finalUrl += customQuery;

    return finalUrl;
  }, [tableConfig, debouncedQuery, pageIndex, getPageMode, pageSize]);

  // Leaf column id -> property path the API sorts on. `initialSort` entries that don't
  // correspond to a column (e.g. createdDate on the leads table) pass through as-is.
  const sortKeyMap = useMemo(() => buildSortKeyMap(tableConfig.columns), [tableConfig.columns]);

  const getSort = useCallback(() => {
    let sort: any = {};

    if (sorting.length != 0) {
      sort = { ...sorting[0], id: sortKeyMap[sorting[0].id] ?? sorting[0].id };
    }

    return sort;
  }, [sorting, sortKeyMap]);


  const requestBody = useMemo(() => {
    return {
      ...getSort(),
      searchBody: {
        ...debouncedAdditionalBody
      }
    };
  }, [sorting, tableConfig.columns, debouncedAdditionalBody]);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["pagedTable", tableConfig.url, ...(tableConfig.queryKey ? [tableConfig.queryKey] : []), currentPageMode, debouncedAdditionalBody, ...pageParams],
    ({ signal }) => {
      // Cancel any previous request
      cleanup();
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      return post<any>(getUrl(), requestBody, abortControllerRef.current.signal) as Promise<IPagedResponse<T>>;
    },
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2, // 2 minutes
      refetchInterval: 1000 * 60 * 5, // 5 minutes
      retry: 1, // Limit retries to prevent infinite loops
      retryDelay: 1000, // Wait 1 second before retrying
      onError: (error) => {
        console.error('PagedTable query error:', error);
      },
    },
  );

  useEffect(() => {
    if (data?.data.rows && setRowData) setRowData(data.data.rows);
  }, [data?.data.rows]);

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  // `sorting` is primarily what we send to the server, so callers may sort on a field that
  // isn't rendered as a column (e.g. createdDate). Only pass react-table entries it can
  // resolve, otherwise getSortedRowModel errors with "Column with id '...' does not exist".
  const tableSorting = useMemo(
    () => sorting.filter((sort) => sort.id in sortKeyMap),
    [sorting, sortKeyMap]
  );

  const table = useReactTable({
    // Must be a stable reference: a fresh [] each render makes react-table reset the page index, which
    // re-renders, which makes a fresh [] ... - an endless loop that locks the tab whenever there's no data yet.
    data: data?.data.rows ?? EMPTY_ROWS,
    columns: tableConfig.columns,
    state: {
      sorting: tableSorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex,
    // Provide our updateData function to our table meta
    meta: {
      updateData: (rowIndex, columnId, value) => {
        // Skip page index reset until after next rerender
        skipAutoResetPageIndex();
        setRowData && setRowData((old: T[]) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex]!,
                [columnId]: value,
              }
            }
            return row
          })
        )
      },
    },
  });

  useEffect(() => {
    if (data && !isLoading) {
      table.setPageSize(data.data.pageSize);
    }
  }, [data?.data?.pageSize, isLoading]);

  useEffect(() => {
    console.log("Sorting changed:", getSort());
  }, [sorting]);

  const changeQuery = (newQuery: string) => {
    if (isLoading) return;
    setQuery(newQuery);
    setPageIndex(1);
  };

  const changePage = (increment: number) => {
    if (isLoading) return;
    setPageIndex(pageIndex + increment);
  }

  const handleToggle = (index: number) => {
    if (isLoading) return;

    let updatedToggles = [...toggles];

    // Determine the new state for the toggle at the given index
    let toggleNewState = !updatedToggles[index];

    // If the toggle is a page mode toggle, set all other page mode toggles to false
    if (tableConfig.modeToggles![index].pageMode) {
      updatedToggles = updatedToggles.map((currentToggle, i) =>
        tableConfig.modeToggles![i].pageMode ? false : currentToggle
      );
    }

    // Set the toggle at the index to its new state
    updatedToggles[index] = toggleNewState;
    setToggles(updatedToggles);
  }

  // useEffect(() => {
  //   let rows = table.getRowModel();
  // }, [table.getSelectedRowModel()]);

  // const lastUrlRef = useRef<string | null>(null);
  // const lastBodyRef = useRef<string | null>(null);

  // const memoizedUrl = useMemo(() => getUrl(), [getUrl]);
  // const memoizedBody = useMemo(() => JSON.stringify(requestBody), [requestBody]);

  // useEffect(() => {
  //   if (data && !isLoading && !isFetching) {
  //     lastUrlRef.current = memoizedUrl;
  //     lastBodyRef.current = memoizedBody;
  //   }
  // }, [data, isLoading, isFetching, memoizedUrl, memoizedBody]);

  // Determine if we should show loading - only show loading for initial load or when explicitly refetching
  const shouldShowLoading = isLoading || (isFetching && !data);

  // Determine if we should show error state
  const shouldShowError = error && !data?.data?.rows?.length && !isLoading;
  
  // Check if error is an abort error (which is expected during cleanup)
  const isAbortError = error && (
    (error as any)?.name === 'AbortError' || 
    (error as any)?.message?.includes('aborted')
  );
  
  // Show error state only if we have a real error and no previous data
  if (shouldShowError && !isAbortError) {
    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">Error loading data. Please try again.</p>
        <ButtonInput 
          label="Retry"
          onClick={() => refetch()}
          classes="mt-2" isSubmit={false}        />
      </div>
    );
  }

  const searchBar = (
    tableConfig.queryLabel ? <div className="relative flex items-stretch w-full sm:w-auto sm:flex-grow focus-within:z-10">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <MagnifyingGlassIcon
          className="h-5 w-5 text-gray-400"
          aria-hidden="true"
        />
      </div>
      <input
        type="text"
        disabled={isLoading}
        onChange={(e) => changeQuery(e.target.value)}
        className="pl-10 sm:text-sm w-full sm:w-auto sm:min-w-[250px] max-w-full"
        placeholder={tableConfig.queryLabel!}
        style={!isMobile ? { width: `calc(${tableConfig.queryLabel!.length}ch - 2rem)` } : undefined}
      />
    </div> : null
  );

  return (
    <div>
      {!tableConfig.condensedView && (
        <div className={`mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}>

          <div className="flex flex-wrap items-center gap-3 sm:gap-x-5">

            {searchBar}

            {toggles?.map((toggle, i) =>
              <div className="flex items-center" key={i}>
                <input onChange={() => handleToggle(i)} checked={toggle} type="checkbox" className="mr-2 h-5 w-5" />
                <label >{tableConfig.modeToggles![i].label}</label>
              </div>
            )}

          </div>

          {tableConfig.showTotal &&
            <div className="flex items-center gap-2">
              <div className="text-sm">Total Rows: {data?.data.total}</div>
            </div>
          }
        </div>
      )}
      {tableConfig.actionButtons && tableConfig.actionButtons?.length > 0 && (
        <div className={`flex flex-wrap gap-2 sm:gap-4 mt-3`}>
          {tableConfig.actionButtons?.map((button, i) =>
            button.isLoading ?
              <ButtonInput key={i} disabled={true} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={"!bg-primary-600 px-3 py-2 text-white"}>
                <ArrowPathIcon className="animate-spin bg-primary-600 h-5 w-5" />
              </ButtonInput>
              :
              <ButtonInput key={i} label={button.label} disabled={button.disabledOnNoRows ? table.getSelectedRowModel().rows.length === 0 : false} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={""} />
          )}
        </div>
      )}
      <div className={`${tableConfig.condensedView ? '' : 'mt-4'} bg-white`}>
        <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">

          {tableConfig.condensedView ? (
            <div className="w-full sm:w-1/3">{searchBar}</div>
          ) : (
            <div className="px-4 sm:pl-4 sm:pr-0 sm:w-1/3 text-sm sm:text-base">
              {table.getPreFilteredRowModel()?.rows?.length} Rows
            </div>
          )}
          

          <div className="flex items-center gap-2 px-4 sm:px-0 sm:justify-center sm:w-1/3">
            <ButtonInput label={"<"} isSubmit={false} onClick={() => changePage(-1)}
              disabled={!(pageIndex > 1)} classes="text-xl"
            />

            <ButtonInput label={">"} isSubmit={false} onClick={() => changePage(1)}
              disabled={!(pageIndex < data?.data?.totalPages!)} classes="text-xl"
            />

            <span className="flex items-center gap-1 whitespace-nowrap">
              <div>Page</div>
              <strong>
                {data?.data.page} of{' '}
                {data?.data.totalPages}
              </strong>
            </span>
          </div>

          <div className={`flex flex-wrap items-center gap-2 sm:gap-4 px-4 sm:pl-0 ${tableConfig.condensedView ? 'sm:pr-0' : 'sm:pr-4'}`}>

            <span className="flex items-center gap-1 whitespace-nowrap">
              Go to page:
              <input
                type="number"
                defaultValue={pageIndex}
                onChange={e => {
                  // unsure why -1 was here, but it meant that the page was always off by 1; just an oscar thing ig
                  // let page = e.target.value ? Number(e.target.value) - 1 : 0;
                  let page = e.target.value ? Number(e.target.value) : 0; 
                  page = page < 1 || page > data?.data.totalPages! ? 1 : page;
                  setPageIndex(page);
                }}
                className="border p-1 rounded w-16"
              />
            </span>

            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
              }}
              className="border p-1 rounded w-28"
            >
              {[10, 30, 50, 100, 200, 1000].map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>

            {tableConfig.condensedView && (
              <div className="text-sm sm:ml-4 whitespace-nowrap">
                Rows: {table.getPreFilteredRowModel()?.rows?.length} of {data?.data.total}
              </div>
            )}

            {tableConfig.insertEmptyRowUrl !== undefined && (
              <ButtonInput 
                label={"+"} 
                isSubmit={false} 
                onClick={() => {
                  post<any>((tableConfig.insertEmptyRowUrl as string), {}).then((res: DefaultResponseWithData<T>) => {
                    setPageIndex(1);
                    refetch();
                  })
                }}
              />
            )}
          </div>
        </div>

        <div className={`${tableConfig.containerClassName ?? ""} rounded-lg shadow-lg overflow-x-auto`}>
          <table className={`min-w-full divide-y border divide-gray-300 ${tableConfig.fontSize ?? ""}`}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={`bg-primary-50 text-gray-800 sticky top-0 z-10`}>
                  {headerGroup.headers.map((header, i) => (
                    <th
                      key={header.id}
                      scope="col"
                      colSpan={header.colSpan}
                      className={
                        `py-3.5 px-3 font-semibold lg:table-cell 
                        ${tableConfig.fontSize ?? "text-sm"} 
                        ${header.colSpan > 1 ? "text-center" : "text-left"} 
                        ${(header.column.columnDef.meta ?? {} as any).headerClassName ?? ""}`
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none hyphens-auto break-words'
                                : 'hyphens-auto break-words',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>

                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {shouldShowLoading ? (
                <tr>
                  <td colSpan={12}>
                    <div className="flex justify-center py-4">
                      <LoadingWheel />
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, i) => {
                  let rowCells = row.getVisibleCells();
                  // Use a stable identifier for the row key - prefer actual ID if available
                  const rowKey = (row.original as any)?.id || row.id;
                  return (
                    <Fragment key={rowKey}>
                      <tr
                        key={rowKey}
                        onClick={() => tableConfig.onRowClick ? tableConfig.onRowClick(row.original) : null}
                        className={`hover:bg-gray-100 hover:cursor-pointer ${tableConfig.rowColor?.(row.original) ?? ""}`}
                      >
                        {rowCells.map((cell) => (
                          <td key={cell.id} className={`${tableConfig.cellPadding ?? "px-3 py-3"} border ${tableConfig.fontSize ?? "text-sm"}`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                      {row.getIsExpanded() && (
                        <tr key={`${rowKey}-expanded`}>
                          {/* 2nd row is a custom 1 cell row */}
                          <td colSpan={row.getVisibleCells().length}>
                            {tableConfig.expandedRow ? tableConfig.expandedRow(row.original) : null}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}